import React, { memo } from 'react';
import { Box, SelectChangeEvent, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import NumberField from './fields/NumberField';
import SelectField from './fields/SelectField';
import StringField from './fields/StringField';
import BooleanField from './fields/BooleanField';
import FieldActionButtons from './FieldActionButtons';
import {
  ControlModeTemplateField,
  ControlModeTemplateFieldsType,
} from '@src/types/ControlModes';
import { FormValue } from '../context/formReducer';

interface FieldItemProps {
  field: ControlModeTemplateField;
  hasAttemptedSubmit: boolean;
  fieldErrors: Record<string, string>;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange:
    | ((field: string, value: FormValue) => void)
    | ((event: SelectChangeEvent<string>) => void);
}

const FieldItem: React.FC<FieldItemProps> = ({
  field,
  hasAttemptedSubmit,
  fieldErrors,
  handleChange,
  handleSelectChange,
}) => {
  const fieldValue = field.defaultValue;

  // Check if this field has an error
  const hasError = hasAttemptedSubmit && !!fieldErrors[field.controllerField];
  const errorMessage = hasError ? fieldErrors[field.controllerField] : '';

  const handleSelectChangeAdapter = (event: SelectChangeEvent<string>) => {
    // Check if handleSelectChange is a function that takes a SelectChangeEvent
    if (handleSelectChange.length === 1) {
      (handleSelectChange as (event: SelectChangeEvent<string>) => void)(event);
    } else {
      // Otherwise, it's a function that takes field and value
      (handleSelectChange as (field: string, value: FormValue) => void)(
        event.target.name,
        event.target.value,
      );
    }
  };

  const renderFieldInput = () => {
    switch (field.type) {
      case ControlModeTemplateFieldsType.SELECT:
        return (
          <SelectField
            field={field}
            fieldValue={fieldValue}
            hasError={hasError}
            errorMessage={errorMessage}
            handleSelectChange={handleSelectChangeAdapter}
          />
        );
      case ControlModeTemplateFieldsType.NUMBER:
        return (
          <NumberField
            field={field}
            fieldValue={fieldValue}
            hasError={hasError}
            errorMessage={errorMessage}
            handleChange={handleChange}
          />
        );
      case ControlModeTemplateFieldsType.BOOLEAN:
        return (
          <BooleanField
            field={field}
            fieldValue={fieldValue}
            hasError={hasError}
            errorMessage={errorMessage}
            handleChange={handleChange}
          />
        );
      case ControlModeTemplateFieldsType.STRING:
      default:
        return (
          <StringField
            field={field}
            fieldValue={fieldValue}
            hasError={hasError}
            errorMessage={errorMessage}
            handleChange={handleChange}
          />
        );
    }
  };

  return (
    <Grid2
      size={6}
      sx={{
        mt: 1,
        padding: '10px',
        borderRadius: '3px',
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant='body2' gutterBottom>
          {field.uiLabel}
        </Typography>
        <Typography variant='caption' gutterBottom sx={{ opacity: 0.6 }}>
          ({field.type})
        </Typography>
      </Box>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        {renderFieldInput()}
        <FieldActionButtons field={field} />
      </Box>
    </Grid2>
  );
};

// Memoize the FieldItem component to prevent unnecessary re-renders
export default memo(FieldItem, (prevProps, nextProps) => {
  // Prevents unnecessary re-renders by comparing only field-specific properties rather than entire object references
  return (
    prevProps.field.controllerField === nextProps.field.controllerField &&
    prevProps.field.defaultValue === nextProps.field.defaultValue &&
    prevProps.field.type === nextProps.field.type &&
    prevProps.hasAttemptedSubmit === nextProps.hasAttemptedSubmit &&
    prevProps.field.quickConfig === nextProps.field.quickConfig &&
    prevProps.fieldErrors[prevProps.field.controllerField] ===
      nextProps.fieldErrors[nextProps.field.controllerField]
  );
});
